import { CalculatorSection } from "@/components/public/CalculatorSection"
import { CtaSection } from "@/components/public/CtaSection"
import { LandingFooter } from "@/components/public/LandingFooter"
import { PublicHeader } from "@/components/public/PublicHeader"
import { StepsSection } from "@/components/public/StepsSection"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { BreathingGradient } from "@/components/ui/living-brand"
import { Heading, Text } from "@/components/ui/typography"
import { useReducedMotion } from "@/hooks/useReducedMotion"
import { cn } from "@/lib/utils"
import { semanticTokens } from "@packages/config/theme"
import { Page } from "@supabank/ui"
import { motion } from "framer-motion"
import {
  ArrowRight,
  BarChart3,
  Building2,
  CheckCircle2,
  FileText,
  Globe,
  Shield,
  Users,
  Zap
} from "lucide-react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

const iconMap = {
  Users,
  BarChart3,
  FileText,
  Shield,
  Zap,
  Globe
}

export default function ForBusinessPage() {
  const prefersReducedMotion = useReducedMotion()
  const { t } = useTranslation("marketing")

  // Get translated use cases from i18n
  const useCases = (t("forBusinessPage.useCases", { returnObjects: true }) as Array<{
    title: string
    description: string
    icon: keyof typeof iconMap
  }>)

  // Get translated benefits from i18n
  const benefits = (t("forBusinessPage.benefits", { returnObjects: true }) as Array<{
    title: string
    description: string
    icon: keyof typeof iconMap
  }>)

  return (
    <Page data-ux-contract="canon" role="main" className="bg-background text-foreground">
      <PublicHeader />

      <main className="space-y-0" id="main" role="main">
        {/* Hero Section - David-focused */}
        <section className={cn(
          "relative overflow-hidden",
          "py-16 sm:py-24 xl:py-32",
          // CANON: Hero gradient background (lavenderGlow for fintech feel)
          semanticTokens.heroGradient.lavenderGlow.tailwind,
          // Dark mode variant
          "dark:bg-black dark:bg-[radial-gradient(ellipse_at_20%_50%,hsla(270,95%,75%,0.08)_0%,transparent_50%),radial-gradient(ellipse_at_80%_80%,hsla(217,91%,60%,0.06)_0%,transparent_50%),hsl(var(--background))]"
        )}>
          {/* Living Brand: Breathing Gradient (syncs with LED heartbeat) - DISABLED on dark theme */}
          <BreathingGradient
            variant="lavender"
            syncWithLed
            className="absolute inset-0 z-[1] pointer-events-none opacity-30 dark:hidden"
          />
          <div className="relative z-10 container mx-auto px-4 sm:px-6">
            <div className="max-w-4xl mx-auto text-center space-y-6">
              <Badge variant="outline" className="mb-4">
                {t("forBusinessPage.badge")}
              </Badge>

              <Heading level={1} className="mb-4">
                {t("forBusinessPage.headline")}
              </Heading>

              <Text variant="lg" color="muted" className="max-w-2xl mx-auto">
                {t("forBusinessPage.subheadline")}
              </Text>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
                <Button asChild size="lg" className="gap-2 min-h-[44px] touch-manipulation">
                  <Link to="/auth">
                    {t("forBusinessPage.cta.scheduleDemo")}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="min-h-[44px] touch-manipulation">
                  <a
                    href="#calculator"
                    onClick={(e) => {
                      e.preventDefault();
                      const element = document.getElementById('calculator');
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                  >
                    {t("forBusinessPage.cta.seePricing")}
                  </a>
                </Button>
              </div>

              {/* Trust signals */}
              <div className="flex flex-wrap justify-center gap-6 mt-12 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  <span>{t("forBusinessPage.trustSignals.amlKyc")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  <span>{t("forBusinessPage.trustSignals.auditTrails")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  <span>{t("forBusinessPage.trustSignals.countries")}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Use Cases Section */}
        <section className={cn("py-16 sm:py-24", semanticTokens.bg.default)}>
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-6xl mx-auto space-y-12">
              <motion.div
                className="text-center space-y-4"
                initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                whileInView={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.24 }}
              >
                <Heading level={2}>{t("forBusinessPage.useCasesSection.headline")}</Heading>
                <Text variant="lg" color="muted">
                  {t("forBusinessPage.useCasesSection.subheadline")}
                </Text>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {useCases.map((useCase, index) => {
                  const Icon = iconMap[useCase.icon] || Users
                  return (
                    <motion.div
                      key={useCase.title}
                      initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                      whileInView={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.24, delay: index * 0.1 }}
                    >
                      <Card className="h-full">
                        <CardContent className="p-6 space-y-3">
                          <div className={cn(
                            "w-12 h-12 rounded-lg flex items-center justify-center",
                            semanticTokens.primary.bg.replace("bg-", "bg-"),
                            semanticTokens.primary.fg.replace("text-", "text-")
                          )}>
                            <Icon className="w-6 h-6" />
                          </div>
                          <Heading level={3} className="text-lg">
                            {useCase.title}
                          </Heading>
                          <Text variant="sm" color="muted">
                            {useCase.description}
                          </Text>
                        </CardContent>
                      </Card>
                    </motion.div>
                  )
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className={cn("py-16 sm:py-24", semanticTokens.bg.surface)}>
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-6xl mx-auto space-y-12">
              <motion.div
                className="text-center space-y-4"
                initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                whileInView={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.24 }}
              >
                <Heading level={2}>{t("forBusinessPage.benefitsSection.headline")}</Heading>
                <Text variant="lg" color="muted">
                  {t("forBusinessPage.benefitsSection.subheadline")}
                </Text>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {benefits.map((benefit, index) => {
                  const Icon = iconMap[benefit.icon] || Shield
                  return (
                    <motion.div
                      key={benefit.title}
                      initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                      whileInView={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.24, delay: index * 0.1 }}
                    >
                      <Card className="h-full">
                        <CardContent className="p-6 space-y-3">
                          <div className={cn(
                            "w-12 h-12 rounded-lg flex items-center justify-center",
                            semanticTokens.primary.bg.replace("bg-", "bg-"),
                            semanticTokens.primary.fg.replace("text-", "text-")
                          )}>
                            <Icon className="w-6 h-6" />
                          </div>
                          <Heading level={3} className="text-lg">
                            {benefit.title}
                          </Heading>
                          <Text variant="sm" color="muted">
                            {benefit.description}
                          </Text>
                        </CardContent>
                      </Card>
                    </motion.div>
                  )
                })}
              </div>
            </div>
          </div>
        </section>

        {/* How It Works - Simplified */}
        <StepsSection id="how-it-works" />

        {/* Calculator Section */}
        <CalculatorSection id="calculator" />

        {/* Enterprise CTA */}
        <section className={cn("py-16 sm:py-24", semanticTokens.bg.default)}>
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-3xl mx-auto text-center space-y-6">
              <motion.div
                initial={prefersReducedMotion ? {} : { opacity: 0 }}
                whileInView={prefersReducedMotion ? {} : { opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.24 }}
                className="space-y-4"
              >
                <div className={cn(
                  "inline-flex items-center justify-center w-16 h-16 rounded-full mb-4",
                  semanticTokens.primary.bg.replace("bg-", "bg-")
                )}>
                  <Building2 className={cn("w-8 h-8", semanticTokens.primary.fg.replace("text-", "text-"))} />
                </div>
                <Heading level={2}>{t("forBusinessPage.enterpriseCta.headline")}</Heading>
                <Text variant="lg" color="muted">
                  {t("forBusinessPage.enterpriseCta.subheadline")}
                </Text>
                <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
                  <Button asChild size="lg" className="gap-2">
                    <Link to="/auth">
                      {t("forBusinessPage.enterpriseCta.contactSales")}
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="lg">
                    <a href={`mailto:${t("forBusinessPage.enterpriseCta.email")}`}>
                      {t("forBusinessPage.enterpriseCta.email")}
                    </a>
                  </Button>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <CtaSection id="cta" />
      </main>

      <LandingFooter />
    </Page>
  )
}
